import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { TriangleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

import { Icon } from './kit';

/**
 * Ecran affiche quand l'URL de l'API n'est pas configuree.
 *
 * Il remplace l'application entiere plutot que d'apparaitre dedans, et c'est
 * voulu : sans URL d'API, il n'y a rien a administrer. Un backoffice qui
 * demarrerait quand meme afficherait un ecran de connexion qui repond « échec »
 * a chaque tentative, sans jamais nommer la cause.
 *
 * Les deux variables sont nommees explicitement, avec l'endroit ou les
 * renseigner : c'est la seule information utile a ce stade.
 *
 * Il est rendu hors de React Admin, sous le theme du mode prefere par le
 * systeme (`main.tsx`), et en Nunito : c'est la page d'amorcage, la premiere
 * que voit un exploitant qui deploie l'image.
 */
export const ConfigurationError = ({ message }: { message: string }): ReactElement => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      backgroundColor: 'background.default',
    }}
  >
    <Card sx={{ width: '100%', maxWidth: 620 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box component="span" sx={{ display: 'inline-flex', color: 'tokens.warning' }}>
              <Icon icon={TriangleAlert} size={32} />
            </Box>
            <Typography variant="h5" component="h1">
              Configuration incomplète
            </Typography>
          </Stack>

          <Typography variant="body1">{message}</Typography>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              En conteneur
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              Renseignez <code>MIAMIAM_API_URL</code> dans l’environnement du service{' '}
              <code>backoffice</code>. L’entrypoint la réécrit dans{' '}
              <code>runtime-config.js</code> au démarrage : une même image sert donc tous les
              environnements, et aucune URL n’est figée à la compilation.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              En développement
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              Créez <code>backoffice/.env.local</code> avec{' '}
              <code>VITE_API_URL=http://localhost:8080/api/v1</code>, puis relancez{' '}
              <code>npm run dev</code>.
            </Typography>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Aucune valeur par défaut n’est appliquée volontairement : une URL devinée
            fonctionnerait sur le poste de son auteur et échouerait partout ailleurs, avec une
            erreur réseau illisible pour seule explication.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  </Box>
);
